import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, type SupportedLang } from "@/i18n";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
  variant?: "header" | "compact";
};

export default function LanguageSwitcher({ className, variant = "header" }: Props) {
  const { i18n, t } = useTranslation();
  const current = (i18n.language?.split("-")[0] as SupportedLang) || "en";

  const change = (lng: SupportedLang) => {
    if (lng === current) return;
    i18n.changeLanguage(lng);
  };

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 border border-white/15 bg-black/40 px-1 py-1 transition-colors hover:border-lime-300/40",
        className,
      )}
      role="group"
      aria-label={t("language.label")}
    >
      <Globe className="h-3.5 w-3.5 text-white/50 ml-1.5" aria-hidden />
      {SUPPORTED_LANGUAGES.map((lng) => {
        const active = lng === current;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => change(lng)}
            aria-pressed={active}
            aria-label={`${lng.toUpperCase()} - ${t("language.switchTo", {
              lang: t(`language.${lng === "en" ? "english" : lng === "pl" ? "polish" : "chinese"}`),
            })}`}
            className={cn(
              "font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 transition-colors",
              active
                ? "bg-lime-300 text-black"
                : "text-white/60 hover:text-lime-200",
            )}
          >
            {lng}
          </button>
        );
      })}
    </div>
  );
}
